import { tipTapBaseExtensions } from '@lessonup/pins-shared';
import {
  color,
  getComponentColors,
  getInputFieldBorderAndColorStyles,
  getInputFieldFontStyles,
  inputFieldPaddingSize,
  InputLabel,
  InputValidationType,
  rem,
  SpaceBetween,
  spacing,
  spacingScaled,
  styled,
  Validation,
} from '@lessonup/ui-components';
import { sanitizeHtml, uniqueId } from '@lessonup/utils';
// eslint-disable-next-line import/no-named-as-default -- This is how TipTap is designed
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface CappedTextAreaProps {
  field: string;
  label: string;
  instructions?: string;
  size?: 'small' | 'large';
  required?: boolean;
  parseEmptyStringAsUndefined?: boolean;
  placeholder?: string;
  maxLength?: number;
  startWarningAt?: number;
  disabled?: boolean;
}

export const CappedTextArea = ({
  label,
  size = 'large',
  placeholder,
  field,
  required,
  maxLength = 10000,
  startWarningAt = 1000,
  parseEmptyStringAsUndefined = false,
  instructions,
  disabled,
}: CappedTextAreaProps) => {
  const { t } = useTranslation(['chatGPT']);
  const { register, watch, setValue } = useFormContext();

  const promptCharacterLimitForWarning = maxLength - startWarningAt;
  const promptInputLength = watch(field)?.length ?? 0;
  const promptMinCharacterLimit = required ? 1 : 0;
  const watchedValue = watch(field);

  const validationType: InputValidationType =
    (promptInputLength > 0 && promptInputLength < promptMinCharacterLimit) || promptInputLength > maxLength
      ? 'negative'
      : 'informative';

  const validationMessage =
    promptInputLength < promptMinCharacterLimit && promptInputLength > 0
      ? t('promptCharactersMinWarning', { promptInputLength, promptMinCharacterLimit })
      : promptInputLength >= promptCharacterLimitForWarning
        ? t('promptCharactersWarning', { promptInputLength, promptCharacterLimit: maxLength })
        : instructions
          ? instructions
          : null;

  const editor = useEditor({
    extensions: [
      ...tipTapBaseExtensions,
      Placeholder.configure({
        placeholder: placeholder || '',
      }),
    ],
    content: watch(field) || '',
    editable: !disabled,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      const content = editor.getHTML();
      const processedContent = parseEmptyStringAsUndefined && content.trim() === '' ? undefined : content;
      setValue(field, processedContent, { shouldValidate: true });
    },
    editorProps: {
      attributes: {
        'data-skip-undo-redo': 'true',
      },
      transformPastedHTML(dirtyHtml) {
        const cleanHtml = sanitizeHtml(dirtyHtml, {
          allowedTags: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li'],
          exclusiveFilter: function (frame) {
            return frame.tag === 'b' && frame.attribs.style?.includes('font-weight:normal') ? 'excludeTag' : false;
          },
        });
        return cleanHtml;
      },
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    const currentValue = watchedValue;
    const editorContent = editor.getHTML();

    // Only update if the content is actually different to avoid infinite loops
    if (!!currentValue && currentValue !== editorContent) {
      editor.commands.setContent(currentValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when watchedValue changes
  }, [watchedValue]);

  useEffect(() => {
    register(field, {
      required,
      maxLength,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, []);

  useEffect(() => {
    if (editor) {
      editor.commands.focus();
    }
  }, [editor]);

  const validationId = uniqueId(`${field}-validation`);

  return (
    <SpaceBetween direction="y" spacing={spacing.size2} size="medium">
      {label && <InputLabel size="medium">{label}</InputLabel>}
      <InputWrapperStyle>
        <StyledEditorContent editor={editor} $minHeight={size === 'large' ? '400px' : '90px'} />
      </InputWrapperStyle>
      {validationMessage && (
        <Validation
          id={validationId}
          size="medium"
          colorMode="neutral"
          type={validationType}
          message={validationMessage}
        />
      )}
    </SpaceBetween>
  );
};

const InputWrapperStyle = styled.div`
  ${getInputFieldBorderAndColorStyles(getComponentColors('neutral'), 'medium')};
  padding: ${spacingScaled(inputFieldPaddingSize, 'medium')};
  overflow: auto;
  resize: vertical;

  .ProseMirror {
    ${getInputFieldFontStyles('medium')};
    height: var(--tiptap-input-height);
    overflow: var(--tiptap-input-overflow);
    outline: none;
  }

  .ProseMirror p {
    margin: 0;
    padding: 0;
  }

  .ProseMirror p,
  .ProseMirror ul,
  .ProseMirror ol {
    + p,
    + ul,
    + ol {
      margin-top: ${spacing.size16};
    }
  }

  .ProseMirror ol,
  .ProseMirror ul {
    margin: 0;
  }

  .ProseMirror p.is-editor-empty:first-of-type::before {
    content: attr(data-placeholder);
    color: ${color.highContrast.disabled.text};
    float: left;
    height: 0;
    pointer-events: none;
  }
`;

const StyledEditorContent = styled(EditorContent, {
  shouldForwardProp: (prop) => prop !== '$minHeight',
})<{ $minHeight: string }>`
  > div {
    min-height: ${({ $minHeight }) => rem($minHeight)};
  }
`;
